import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { doFindUsersAffiliation, luHandleAppiError, type U2104Affiliation, type U2104User } from "../../../U2104Service";
import { U2000FormStateEnumEditMode } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000HandleApiError } from "../../../../u2000Core/axios/U2000HandleApiError";
import { NS } from "../../../U2104";
import type { AxiosInstance } from "axios";
import type { TFunction } from "i18next";
import type { ToastContextType } from "../../../../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../../../../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../../../../u2000Core/utils/u2000ExtractAutoCompleteResults";

// Gestion des handlers pour les actions d'une unité logique
export interface U2104ImportAffiliationHandler {
  importUser: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104Affiliation>>;
  onSearch?: (criterias: any) => Promise<any[]>;
}

// Paramètres pour les handlers des actions d'une unité logique
export interface U2104ImportUserHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104Affiliation>>;
  toast: ToastContextType;
}

// Gestion des handlers pour importer d'un usager
export interface U2104ImportUserHandlerReturn {
  importUser: () => void;
  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104Affiliation>>;
  onSearch?: (criterias: any) => Promise<any[]>;
}

// Implémentation des handlers pour les actions d'importation d'un usager
export function u2104ImportUserHandler(params: U2104ImportUserHandlerParam): U2104ImportUserHandlerReturn {
  const { formStateHandlers, u2000AxiosAuthenticated, t, toast } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);

  // Gestion de l'ajout d'un usager
  const importUser = (): void => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
    return;
  };

  // Gestion de la fermeture du dialog de modification/ajout
  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      formStateHandlers.cancelData();
    } else if (result === "save") {
      formStateHandlers.saveData();
    }

    return;
  };

  // Recherche des usagers pour l'importation
  const onSearch = async (criterias: any): Promise<U2000AutoCompleteSearchResult<U2104User>> => {
    try {
      const response = await doFindUsersAffiliation(u2000AxiosAuthenticated, criterias);
      const extracted = u2000ExtractAutoCompleteResults<U2104User & { id: number; userId: number }>(response.data);
      return {
        ...extracted,
        results: extracted.results.map((user) => ({ ...user })),
      };
    } catch (error: any) {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return { results: [], totalCount: 0, truncated: false };
    }
  };

  return {
    importUser,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    formStateHandlers,
    onSearch: onSearch as unknown as (criterias: any) => Promise<any[]>,
  };
}
